import {
    Autocomplete,
    Box,
    Button,
    Card, CardContent,
    Chip,
    Container,
    Divider,
    FormGroup,
    Grid,
    ImageList,
    ImageListItem,
    Stack, TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useStoreState } from 'pullstate';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BackdropLoader from '../../components/BackdropLoader.jsx';
import CommentsTimelineGoalForm from '../../components/CommentsTimelineGoalForm.jsx';
import RichTextField from '../../components/RichTextField.jsx';
import { AppStore } from '../../stores/AppStore.js';
import { api, createGoalAPI, sendEmailsAddittionalInfoToCoach, sendEmailsAddittionalInfoToPlayer, sendEmailsGoalNeedsRevision, updateGoalAPI } from '../../utils/apiFunctions';
import { clearFieldError, prepareGoalDataForSave, prepareGoalDataForUpdate, shouldDisableGoalForm, validateGoalFields } from '../../utils/goalFormUtils.js';
import { createLocalArrayObject, updateLocalArrayObjects } from '../../utils/localStorageFunctions.js';
import GoalSteps from './GoalSteps.jsx';
import SideBarOnGoalForm from './SideBarOnGoalForm.jsx';

const GoalForm = (props) => {
    const navigate = useNavigate()
    const { goals, loading, user, peopleBuilders } = useStoreState(AppStore)
    const openAlert = useStoreState(AppStore, s => s.snackbar.openAlert);
    const [selectedDate, setSelectedDate] = useState(null);
    const [goalStatusValue, setGoalStatusValue] = useState('draft')
    const loggedUser = useStoreState(AppStore, s => s.user)
    const { peopleBuilderId } = useParams()
    const [goalId, setGoalId] = useState(useParams().goalId || props.goalId)
    const [goalDisabled, setGoalDisabled] = useState(false)
    const [checkInGoalComments, setCheckInGoalComments] = useState([])
    const [goalComments, setGoalComments] = useState([])
    const [steps, setSteps] = useState([
        {
            step_order: 0,
            label: 'Step 1',
            step: '',
            status: null
        }
    ])
    const [stepsToSave, setStepsToSave] = useState([
        {
            step_order: 0,
            label: 'Step 1',
            step: '',
            status: null
        }
    ])
    const [comment, setComment] = useState('')
    const [goalData, setGoalData] = useState({
        people_builder_id: '',
        goal_name: '',
        goal_term: '',
        goal_importance: '',
        goals_etro: '',
        relationship: '',
        target_date: null,
        goal_approval: '',
        goal_status: 'Draft Goal',
        checkin_status: '',
        comments_id: '',
        steps: [],
        goal_approved_date: null,
        comments: null,

    })
    const [emptyField, setEmptyField] = useState({
        'goal_name': { error: false, errorText: '' },
        'goal_term': { error: false, errorText: '' },
        'goal_importance': { error: false, errorText: '' },
        'goals_etro': { error: false, errorText: '' },
        'relationship': { error: false, errorText: '' },
        'steps': { error: false, errorText: '' },
        'target_date': { error: false, errorText: '' },
    })
    const [isGoalSaved, setIsGoalSaved] = useState(true);
    const userPeopleBuilder = peopleBuilders?.filter(pb => pb.id == peopleBuilderId) || []
    const isCoach = userPeopleBuilder?.[0]?.player?.coach === loggedUser.full_name

    useEffect(() => {
        if (peopleBuilders?.length) {
            getUserPeopleBuilder()
        }
    }, [peopleBuilders])

    useEffect(() => {
        if (goals?.length && goalId) {
            getUserGoal()
        }
    }, [goalId, goals?.length])

    useEffect(() => {
        if (!userPeopleBuilder.length) return
        const disabled = shouldDisableGoalForm({
            goals,
            goalId,
            isGoalSaved,
            fromModal: props.fromModal,
            loggedUser,
            isCoach,
            userPeopleBuilder
        })
        setGoalDisabled(disabled)
    }, [userPeopleBuilder, isCoach, goals, goalId, isGoalSaved, props.fromModal, loggedUser])

    const updateGoalData = (key, value) => {
        setGoalData(prevData => ({ ...prevData, inheritance: false }))
        if (key === 'steps') {
            setGoalData(prevData => ({ ...prevData, steps: value }))
        } else if (key === 'stepsToSave') {
            setStepsToSave(value)
        } else {
            setGoalData(prevData => ({ ...prevData, [key]: value }))
        }
    }

    const handleCommentsChange = useCallback((id, value) => {
        setGoalComments(value)
        setIsGoalSaved(false)
        updateGoalData('comments', value)
    }, [])

    const handleGoalStatusChange = (event, newValue) => {
        if (newValue === null) return; // Prevent deselecting all options

        setGoalStatusValue(newValue);
        setIsGoalSaved(false);

        // Map toggle values to actual goal statuses
        if (isCoach) {
            if (newValue === 'approved') {
                updateGoalData('goal_status', 'Active');
                updateGoalData('goal_approval', true);
                updateGoalData('goal_approved_date', moment().format('YYYY-MM-DD'));
            } else if (newValue === 'revision') {
                updateGoalData('goal_status', 'Needs Revision');
                updateGoalData('goal_approval', false);
                updateGoalData('goal_approved_date', null);
            } else if (newValue === 'draft') {
                updateGoalData('goal_status', 'Draft Goal');
                updateGoalData('goal_approval', false);
                updateGoalData('goal_approved_date', null);
            }
        } else {
            // Player
            if (newValue === 'ready') {
                updateGoalData('goal_status', 'Ready for Approval');
                updateGoalData('goal_approval', true);
            } else if (newValue === 'draft') {
                updateGoalData('goal_status', 'Draft Goal');
                updateGoalData('goal_approval', false);
            }
        }
    };

    const createGoal = async () => {
        AppStore.update(s => { s.loading = true })

        const dataToSave = prepareGoalDataForSave(goalData, goalComments)

        if (validateGoalFields(goalData, steps, setEmptyField)) {
            AppStore.update(s => { s.loading = false })
            return
        }
        const goalCreated = await createGoalAPI(dataToSave)
        if (goalCreated.status === 200) {
            setTimeout(() => {
                navigate(-1)
            }, 1500)
            openAlert('Goal created successfully', 'success')
            createLocalArrayObject('goals', goalCreated.data)
            setIsGoalSaved(true)
            // if (goalCreated.data.goal_status === 'Approval') {
            //     sendEmailsGoalReadyForApproval({ ...goalCreated.data, user: userPeopleBuilder[0] }, loggedUser)
            // }
        } else {
            openAlert(`Oops! Something went wrong: status ${goalCreated.status}`, 'error')
        }
        AppStore.update(s => { s.loading = false })
    }

    const getUserGoal = useCallback(async () => {

        if (!goalId) {
            return
        }
        AppStore.update(s => { s.loading = true })
        const responseUserGoals = goals?.filter(goal => goal.id == goalId)

        try {
            const response = await api.get(`/steps`)
            const step = response.data.filter(step => step.goal_id === responseUserGoals?.[0].id)
            if (step.length) {
                setSteps(step)
                setStepsToSave(step)
            }
            AppStore.update(s => { s.loading = false })
        } catch (error) {
            AppStore.update(s => { s.loading = false })
            console.log(error)
        }

        updateGoalData('people_builder_id', responseUserGoals?.[0]?.people_builder_id)
        updateGoalData('goal_name', responseUserGoals?.[0]?.goal_name)
        updateGoalData('goal_term', responseUserGoals?.[0]?.goal_term)
        updateGoalData('goal_importance', responseUserGoals?.[0]?.goal_importance)
        updateGoalData('goals_etro', responseUserGoals?.[0]?.goals_etro)
        updateGoalData('inheritance', responseUserGoals?.[0]?.inheritance)
        updateGoalData('steps', steps)
        updateGoalData('relationship', responseUserGoals?.[0]?.relationship)
        updateGoalData('goal_approval', responseUserGoals?.[0]?.goal_approval)
        updateGoalData('target_date', responseUserGoals?.[0]?.target_date ? moment(responseUserGoals?.[0]?.target_date?.split('T')[0]) : null)
        setSelectedDate(responseUserGoals?.[0]?.target_date ? moment(responseUserGoals?.[0]?.target_date?.split('T')[0]) : null)
        updateGoalData('goal_approved_date', responseUserGoals?.[0]?.goal_approved_date)
        const existingComments = responseUserGoals?.length ? JSON.parse(responseUserGoals?.[0]?.comments || '[]') : []
        updateGoalData('comments', existingComments)
        setGoalComments(existingComments)
        updateGoalData('goal_status', responseUserGoals?.[0]?.goal_status)

        // Map goal_status to toggle value
        const currentStatus = responseUserGoals?.[0]?.goal_status;
        if (isCoach) {
            if (currentStatus === 'Active') {
                setGoalStatusValue('approved');
            } else if (currentStatus === 'Needs Revision') {
                setGoalStatusValue('revision');
            } else {
                setGoalStatusValue('draft');
            }
        } else {
            if (currentStatus === 'Ready for Approval') {
                setGoalStatusValue('ready');
            } else {
                setGoalStatusValue('draft');
            }
        }

        const disabled = shouldDisableGoalForm({
            goals,
            goalId,
            isGoalSaved,
            fromModal: props.fromModal,
            loggedUser,
            isCoach,
            userPeopleBuilder
        })
        setGoalDisabled(disabled)
    }, [goals, goalId, isGoalSaved, props.fromModal, loggedUser, isCoach, userPeopleBuilder])

    const isAllGoalsActive = useCallback(() => {
        const pbId = userPeopleBuilder[0]?.id
        const goalsPB = goals?.filter(goal =>
            goal.people_builder_id === pbId && goal.goal_status !== 'archived'
        ) || []
        const isAllActive = goalsPB.every(goal => goal.goal_status === 'Active')
        return isAllActive
    }, [goals, userPeopleBuilder])

    const getUserPeopleBuilder = useCallback(async () => {
        try {
            const responseUserPeopleBuilder = peopleBuilders?.filter(pb => pb.id == peopleBuilderId)
            updateGoalData('people_builder_id', responseUserPeopleBuilder?.[0]?.id)
        } catch (error) {
            console.log(error)
        }
    }, [peopleBuilders, loggedUser, peopleBuilderId])

    const updateGoal = async (id) => {
        try {
            AppStore.update(s => { s.loading = true })

            if (validateGoalFields(goalData, steps, setEmptyField)) {
                AppStore.update(s => { s.loading = false })
                console.log('we are here')
                return
            }

            const dataToSave = prepareGoalDataForUpdate(goalData, goalComments)
            const goalUpdated = await updateGoalAPI(id, dataToSave)
       
            const statusUpdated = dataToSave.goal_status != goals.find(goal =>goal.id==id).goal_status

            if (goalUpdated.status === 200) {
                updateLocalArrayObjects('goals', goalUpdated.data.id, goalUpdated.data)

                AppStore.update(s => {
                    const goalIndex = s.goals.findIndex(g => g.id === goalUpdated.data.id)
                    if (goalIndex !== -1) {
                        s.goals[goalIndex] = goalUpdated.data
                    } else {
                        s.goals.push(goalUpdated.data)
                    }
                })

                setIsGoalSaved(true)

                const disabled = shouldDisableGoalForm({
                    goals,
                    goalId,
                    isGoalSaved: true,
                    fromModal: props.fromModal,
                    loggedUser,
                    isCoach,
                    userPeopleBuilder
                })
                setGoalDisabled(disabled)

                openAlert('Goal updated successfully', 'success')

                if (dataToSave.goal_status === 'Active') {
                    const isAllActive = isAllGoalsActive()
                    if (isAllActive) {
                        Promise.all([
                            sendEmailsAddittionalInfoToPlayer(userPeopleBuilder[0], loggedUser),
                            sendEmailsAddittionalInfoToCoach(userPeopleBuilder[0], loggedUser)
                        ]).catch(err => console.error('Error sending emails:', err))
                    }
                } else if (dataToSave.goal_status === 'Needs Revision' && statusUpdated ) {
                    sendEmailsGoalNeedsRevision({ ...dataToSave, user: userPeopleBuilder[0] }, loggedUser)
                        .catch(err => console.error('Error sending email:', err))
                }
                
                setTimeout(() => {
                    AppStore.update(s => { s.loading = false })
                    navigate(-1)
                }, 100)
            } else {
                throw new Error(`Update failed with status: ${goalUpdated.status}`)
            }
        } catch (error) {
            console.error('Error updating goal:', error)
            AppStore.update(s => { s.loading = false })
            openAlert(`Something went wrong updating the goal: ${error.message}`, 'error')
        }
    }

    const matchesSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'))

    return (

        <Container sx={{ mt: props.fromModal ? 0 : 10 }} maxWidth={matchesSmallScreen ? 'sm' : 'xl'} >
            <Stack direction={matchesSmallScreen ? 'column' : 'row'} marginRight='0'>
                <BackdropLoader openBackdrop={loading} />
                <Card sx={{ maxHeight: '85vh', overflow: 'auto', width: '100%' }}>
                    <CardContent>
                        <Stack
                            direction='row'
                            spacing={4}
                            alignItems='center'
                            alignContent='space-between'
                        >
                            <Grid item display={matchesSmallScreen ? 'none' : 'block'}>
                                <ImageList>
                                    <ImageListItem sx={{ width: '10rem' }}>
                                        <img src="https://iili.io/J1MU5vV.png" />
                                    </ImageListItem>
                                </ImageList>
                            </Grid>
                            <Box sx={{ padding: 2, width: '100%' }}>
                                <Stack
                                    direction='row'
                                    spacing={4}
                                    justifyContent='space-between'
                                    alignContent='center'
                                >
                                    <Grid item sm={12}>
                                        <Typography variant='span'>Player:
                                            <Chip label={userPeopleBuilder?.[0]?.player?.full_name || loggedUser.full_name} />
                                        </Typography>
                                    </Grid>
                                    <Typography variant='span'>Coach:
                                        <Chip label={userPeopleBuilder?.[0]?.player?.coach} />
                                    </Typography>
                                    <Typography variant='span'>Year: {userPeopleBuilder?.[0]?.year || new Date().getFullYear()}</Typography>
                                </Stack>
                            </Box>
                        </Stack>
                        <Container>
                            <FormGroup sx={{ mt: 2 }}>
                                <Stack spacing={4}>
                                    {/* <Stack direction='row' spacing={2}> */}
                                    <Grid sx={{ width: '100%' }} container spacing={2}>
                                        <Grid item style={{ paddingLeft: '0px', marginTop: '0px' }} xs={12} sm={6}>
                                            <TextField
                                                disabled={goalDisabled}
                                                id="goal-name outlined-basic"
                                                label="Goal Name"
                                                variant='standard'
                                                value={goalData.goal_name}
                                                fullWidth
                                                onChange={(e) => {
                                                    setEmptyField(clearFieldError(emptyField, 'goal_name'))
                                                    updateGoalData('goal_name', e.target.value)
                                                }}
                                                helperText={emptyField.goal_name.errorText}
                                                error={emptyField.goal_name.error}
                                                required
                                                autoFocus
                                            />
                                        </Grid>
                                        <Grid item style={{ marginTop: '0px' }} xs={12} sm={6}>
                                            <Autocomplete
                                                fullWidth
                                                disabled={goalDisabled}
                                                options={[
                                                    'Short Term (1-3 Months)',
                                                    'Annual Term (12 Months)',
                                                    'Medium Term (1-2 Years)',
                                                    'Long Term (2-5 Years)',
                                                    'Life Long (5+ Years)'
                                                ]}
                                                label="Goal Term"
                                                variant='standard'
                                                value={goalData?.goal_term || null}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        helperText={emptyField.goal_term.errorText}
                                                        error={emptyField.goal_term.error}
                                                        required={goalData.goal_status !== 'Draft Goal' ? true : false}
                                                        label="Goal Term"
                                                        variant="standard"
                                                    />
                                                )}
                                                onChange={(e, newValue) => {
                                                    setEmptyField(clearFieldError(emptyField, 'goal_term'))
                                                    updateGoalData('goal_term', newValue)
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <RichTextField
                                        disabled={goalDisabled}
                                        label="Goal Importance"
                                        placeholder='Explain why this goal matters to you'
                                        value={goalData.goal_importance}
                                        required={goalData.goal_status !== 'Draft Goal'}
                                        helperText={emptyField.goal_importance.errorText}
                                        error={emptyField.goal_importance.error}
                                        onChange={(event) => {
                                            setEmptyField(clearFieldError(emptyField, 'goal_importance'))
                                            updateGoalData('goal_importance', event.target.value)
                                        }}
                                    />
                                    <Grid item style={{ marginTop: '16px' }} xs={12} sm={4}>
                                        <Autocomplete
                                            fullWidth
                                            disabled={goalDisabled}
                                            value={goalData?.goals_etro || null}
                                            options={[
                                                'Culture of Accountability & High Performing Teams',
                                                'Exceptional Delivery',
                                                'High Level of Quality',
                                                'Industry Partners',
                                                'Innovation at Every Level',
                                                'Lean Process',
                                                'People First'
                                            ]}
                                            // label="Goals ETRO"
                                            variant='standard'
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    label="Strategic ETRO Goal"
                                                    variant="standard"
                                                    helperText={emptyField.goals_etro.errorText}
                                                    error={emptyField.goals_etro.error}
                                                    required={goalData.goal_status !== 'Draft Goal' ? true : false}
                                                />
                                            }
                                            onChange={(e, newValue) => {
                                                setEmptyField(clearFieldError(emptyField, 'goals_etro'))
                                                updateGoalData('goals_etro', newValue)
                                            }}

                                        />
                                    </Grid>
                                    <RichTextField
                                        disabled={goalDisabled}
                                        label="Strategic ETRO Goal Explanation"
                                        placeholder='Describe how your goal supports the Strategic ETRO Goal'
                                        value={goalData.relationship}
                                        required={goalData.goal_status !== 'Draft Goal'}
                                        helperText={emptyField.relationship.errorText}
                                        error={emptyField.relationship.error}
                                        onChange={(event) => {
                                            setEmptyField(clearFieldError(emptyField, 'relationship'))
                                            updateGoalData('relationship', event.target.value)
                                        }}
                                    />
                                    <GoalSteps
                                        disabled={goalDisabled}
                                        setGoalId={setGoalId}
                                        goalId={goalId}
                                        setSteps={setSteps}
                                        stepsToSave={stepsToSave}
                                        setStepsToSave={setStepsToSave}
                                        steps={steps}
                                        onSaveSteps={updateGoalData}
                                        goalData={goalData}
                                        emptyField={emptyField}
                                        setEmptyField={setEmptyField}
                                        getUserGoal={getUserGoal}
                                    />
                                    <Stack spacing={4} >
                                        <LocalizationProvider
                                            dateAdapter={AdapterMoment}
                                        >
                                            <DatePicker
                                                label="Target Date"
                                                disabled={goalDisabled}
                                                sx={{ width: '100%' }}
                                                value={selectedDate}
                                                onChange={(newValue) => {
                                                    setSelectedDate(newValue)
                                                    setEmptyField(clearFieldError(emptyField, 'target_date'))
                                                    updateGoalData('target_date', newValue ? newValue.format().split('T')[0] : '')
                                                }}
                                                views={['year', 'month', 'day']}
                                                required
                                                slotProps={{
                                                    textField: {
                                                        helperText: emptyField.target_date.errorText,
                                                        error: emptyField.target_date.error,
                                                        required: goalData.goal_status !== 'Draft Goal' ? true : false
                                                    }
                                                }}

                                                format='YYYY-MM-DD'
                                            />
                                        </LocalizationProvider>
                                        <Box sx={{
                                            padding: 2,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,

                                        }}>
                                            {/* <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                                Goal Status
                                            </Typography> */}
                                            <ToggleButtonGroup
                                                value={goalStatusValue}
                                                exclusive
                                                onChange={handleGoalStatusChange}
                                                disabled={goalDisabled}
                                                color="primary"
                                                sx={{
                                                    '& .MuiToggleButton-root': {
                                                        py: 1.5,
                                                        textTransform: 'none',
                                                        fontWeight: 500
                                                    },
                                                    display: 'flex',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {isCoach ? (
                                                    <>
                                                        {goalId && (
                                                            <ToggleButton sx={{ width: '10rem', borderRadius: '5px 0px 0px 5px' }} value="revision" color="primary">
                                                                Back To Revision
                                                            </ToggleButton>
                                                        )}
                                                        {!goalId && (
                                                            <ToggleButton sx={{ width: '10rem', borderRadius: '5px 0px 0px 5px' }} value="draft" color="primary">
                                                                Draft Goal
                                                            </ToggleButton>
                                                        )}
                                                        <ToggleButton sx={{ width: '10rem', borderRadius: '0px 5px 5px 0px' }} value="approved" color="primary">
                                                            Goal Approved
                                                        </ToggleButton>
                                                    </>
                                                ) : (
                                                    <>
                                                        <ToggleButton sx={{ width: '10rem', borderRadius: '5px 0px 0px 5px' }} value="draft" color="primary">
                                                            Draft Goal
                                                        </ToggleButton>
                                                        <ToggleButton sx={{ width: '10rem', borderRadius: '0px 5px 5px 0px' }} value="ready" color="primary">
                                                            Ready for Approval
                                                        </ToggleButton>
                                                    </>
                                                )}
                                            </ToggleButtonGroup>
                                        </Box>

                                        <CommentsTimelineGoalForm
                                            setComment={setComment}
                                            comment={comment}
                                            handleCommentsChange={handleCommentsChange}
                                            loggedUser={loggedUser}
                                            updateGoalData={updateGoalData}
                                            setCheckInGoalComments={setCheckInGoalComments}
                                            checkInGoalComments={checkInGoalComments}
                                            goalComments={goalComments}
                                            setGoalComments={setGoalComments}
                                            goalId={goalId}
                                            goalDisabled={goalDisabled}
                                            showCheckinComments={false}
                                        />
                                    </Stack>
                                    <Divider />
                                    <Stack direction='row' spacing={2} justifyContent='right'>
                                        <Button
                                            onClick={() => navigate(-1)}
                                            disabled={goalDisabled}
                                            color='error'
                                            variant="contained">Cancel</Button>
                                        <Tooltip title={!isCoach && goalDisabled ? 'This goal is not update by player anymore' : ''}>
                                            <Stack>
                                                <Button
                                                    variant="contained"
                                                    disabled={goalDisabled}
                                                    color='success'
                                                    type='submit'
                                                    onClick={() => {
                                                        goalId ? updateGoal(goalId, goalData) : createGoal()
                                                    }}
                                                >
                                                    Save
                                                </Button>
                                            </Stack>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </FormGroup>
                        </Container>
                    </CardContent>
                </Card>
                <SideBarOnGoalForm />
            </Stack>
        </Container >
    )
}

export default GoalForm